import { Controller } from "@hotwired/stimulus";

const ANIMATION_MS = 150;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
const DRAG_THRESHOLD_PX = 4;
// Include background-color so we don't trample the anchored-cell hover transition.
const FLIP_TRANSITION = `transform ${ANIMATION_MS}ms ${EASING}, background-color ${ANIMATION_MS}ms ${EASING}`;

export default class extends Controller {
  static values = {
    userId: String,
    table: String,
    version: { type: Number, default: 1 },
  };

  connect() {
    this.headerRow = this.element.querySelector("thead tr");
    if (!this.headerRow) return;

    const stored = this.readStored();
    if (stored.order?.length) this.applyOrder(stored.order);
    this.ensureRightAnchor();
    // Responsive tables may connect while hidden behind a mobile list. Wait
    // until the table is visible before freezing its measured column widths.
    const applyVisibleLayout = () => {
      if (!this.element.getClientRects().length) return;
      this.applyLayout(stored.widths || {});
      this.element.toggleAttribute("data-column-order-applied", true);
      this.layoutObserver?.disconnect();
    };
    if (this.element.getClientRects().length) {
      applyVisibleLayout();
    } else {
      this.layoutObserver = new ResizeObserver(applyVisibleLayout);
      this.layoutObserver.observe(this.element);
    }

    this.headerRow.addEventListener("mousedown", this.onColumnMouseDown);
    this.draggableHeaders().forEach((th) => {
      th.addEventListener("mouseenter", this.onColumnHoverIn);
      th.addEventListener("mouseleave", this.onColumnHoverOut);
    });
  }

  disconnect() {
    this.layoutObserver?.disconnect();
    this.headerRow?.removeEventListener("mousedown", this.onColumnMouseDown);
    this.draggableHeaders().forEach((th) => {
      th.removeEventListener("mouseenter", this.onColumnHoverIn);
      th.removeEventListener("mouseleave", this.onColumnHoverOut);
    });
    this.clearHoverColumn();
    document.removeEventListener("mousemove", this.onColumnDragMove);
    document.removeEventListener("mouseup", this.endColumnDrag);
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.endResize);
  }

  onColumnHoverIn = (event) => {
    if (this.colDrag || this.resizing) return;
    this.setHoverColumn(event.currentTarget.dataset.orderableColumn);
  };

  onColumnHoverOut = () => {
    if (this.colDrag || this.resizing) return;
    this.clearHoverColumn();
  };

  setHoverColumn(key) {
    if (this.hoverKey === key) return;
    this.clearHoverColumn();
    this.hoverKey = key;
    this.element
      .querySelectorAll(`[data-orderable-column="${CSS.escape(key)}"]`)
      .forEach((cell) => cell.setAttribute("data-column-hovered", ""));
  }

  clearHoverColumn() {
    if (!this.hoverKey) return;
    this.element
      .querySelectorAll("[data-column-hovered]")
      .forEach((cell) => cell.removeAttribute("data-column-hovered"));
    this.hoverKey = null;
  }

  // Mouse-based drag so swap detection is x-only — the cursor can drift off
  // the header row without breaking the drag.
  onColumnMouseDown = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest("[data-column-resize-handle]")) return;
    const th = event.target.closest("th[data-orderable-column]");
    if (!th) return;

    event.preventDefault();
    this.colDrag = { th, startX: event.clientX, moved: false };
    document.addEventListener("mousemove", this.onColumnDragMove);
    document.addEventListener("mouseup", this.endColumnDrag);
  };

  onColumnDragMove = (event) => {
    if (!this.colDrag) return;
    // Defer commit so a plain click on a sortable header still flips its sort.
    if (!this.colDrag.moved) {
      if (Math.abs(event.clientX - this.colDrag.startX) < DRAG_THRESHOLD_PX) return;
      this.colDrag.moved = true;
      document.body.classList.add("column-gesture-active", "column-gesture-grab");
    }

    const target = this.findSwapTarget(event.clientX);
    if (target) this.swapColumns(this.colDrag.th, target);
  };

  endColumnDrag = () => {
    if (!this.colDrag) return;
    const { moved } = this.colDrag;
    document.body.classList.remove("column-gesture-active", "column-gesture-grab");
    document.removeEventListener("mousemove", this.onColumnDragMove);
    document.removeEventListener("mouseup", this.endColumnDrag);
    this.colDrag = null;

    if (!moved) return;
    // Swallow the trailing click so a drag doesn't also trigger the sort link.
    document.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        e.preventDefault();
      },
      { capture: true, once: true }
    );
    this.writeStored({ order: this.currentHeaderKeys() });
  };

  // Farthest neighbor whose midpoint cursorX crossed — handles fast drags.
  findSwapTarget(cursorX) {
    const headers = this.draggableHeaders();
    const centers = headers.map((th) => {
      const { left, right } = th.getBoundingClientRect();
      return (left + right) / 2;
    });
    const idx = headers.indexOf(this.colDrag.th);
    let target = null;
    for (let i = idx + 1; i < headers.length; i++) {
      if (cursorX < centers[i]) break;
      target = headers[i];
    }
    if (target) return target;
    for (let i = idx - 1; i >= 0; i--) {
      if (cursorX > centers[i]) break;
      target = headers[i];
    }
    return target;
  }

  swapColumns(dragged, target) {
    // Snapshot before mutating so FLIP also animates the column-width
    // reshuffle that fixed-layout triggers when the header reorders.
    const headerCells = [...this.headerRow.children];
    const bodyCellsPerRow = this.bodyRows().map((row) => [...row.children]);
    const headerFirsts = this.captureFirsts(headerCells);
    const bodyFirstsPerRow = bodyCellsPerRow.map((cells) => this.captureFirsts(cells));

    const headers = this.draggableHeaders();
    const draggedIsAfter = headers.indexOf(dragged) > headers.indexOf(target);
    this.headerRow.insertBefore(dragged, draggedIsAfter ? target : target.nextSibling);

    const keys = this.currentHeaderKeys();
    this.bodyRows().forEach((row) => this.reorderRow(row, keys));

    this.flip(headerCells, headerFirsts);
    bodyCellsPerRow.forEach((cells, i) => this.flip(cells, bodyFirstsPerRow[i]));
  }

  // FLIP: invert each cell's pre-swap position, then transition to identity.
  flip(cells, firsts) {
    cells.forEach((cell) => {
      const firstLeft = firsts.get(cell);
      if (firstLeft === undefined) return;
      const delta = firstLeft - cell.getBoundingClientRect().left;
      if (delta === 0) return;
      cell.style.transition = "none";
      cell.style.transform = `translateX(${delta}px)`;
    });
    requestAnimationFrame(() => {
      cells.forEach((cell) => {
        cell.style.transition = FLIP_TRANSITION;
        cell.style.transform = "";
      });
    });
  }

  captureFirsts(elements) {
    return new Map(elements.map((el) => [el, el.getBoundingClientRect().left]));
  }

  startResize = (event) => {
    // Stop the mousedown from reaching our column-drag listener on the row.
    event.preventDefault();
    event.stopPropagation();
    const th = event.currentTarget.closest("th");
    if (!th) return;
    this.resizing = {
      th,
      startX: event.clientX,
      startWidth: th.getBoundingClientRect().width,
      minWidth: parseFloat(getComputedStyle(th).minWidth) || 0,
    };
    document.body.classList.add("column-gesture-active", "column-gesture-resize");
    document.addEventListener("mousemove", this.onResizeMove);
    document.addEventListener("mouseup", this.endResize);
  };

  onResizeMove = (event) => {
    if (!this.resizing) return;
    const delta = event.clientX - this.resizing.startX;
    const width = Math.max(this.resizing.minWidth, this.resizing.startWidth + delta);
    this.resizing.th.style.width = `${width}px`;
  };

  endResize = () => {
    document.body.classList.remove("column-gesture-active", "column-gesture-resize");
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.endResize);
    this.persistWidths();
    this.resizing = null;
  };

  // Inject an empty right-anchored spacer in front of any existing sticky cell
  // (e.g. the actions column). With no width hint, the phantom absorbs leftover
  // space if the user later shrinks columns below the table's natural width.
  // applyLayout rolls its initial auto-allocated width into the last data
  // column so the phantom starts at 0 instead of pinned to the auto-layout slack.
  ensureRightAnchor() {
    const make = (tag) => {
      const el = document.createElement(tag);
      el.dataset.columnAnchor = "right";
      el.dataset.phantomAnchor = "";
      return el;
    };
    const insertBeforeAnchorOrAppend = (parent, cell) => {
      const anchor = parent.querySelector(":scope > [data-column-anchor='right']");
      anchor ? parent.insertBefore(cell, anchor) : parent.appendChild(cell);
    };
    insertBeforeAnchorOrAppend(this.headerRow, make("th"));
    this.bodyRows().forEach((row) => insertBeforeAnchorOrAppend(row, make("td")));
  }

  applyOrder(order) {
    // reorderRow already handles stale keys (skipped) and current-but-unstored
    // keys (appended via byKey.forEach), so no need to pre-merge here.
    this.reorderRow(this.headerRow, order);
    this.bodyRows().forEach((row) => this.reorderRow(row, order));
  }

  // Freeze keyed-column widths under fixed layout. Measure first under
  // auto-layout, then roll the phantom's auto-allocated slack into the last
  // data column so the phantom starts at 0 width. From there it only grows
  // again if the user shrinks columns below the table's natural width.
  applyLayout(storedWidths) {
    const ths = this.draggableHeaders();
    const measured = ths.map((th) => Math.round(th.getBoundingClientRect().width));
    const phantomTh = this.headerRow.querySelector(":scope > [data-phantom-anchor]");
    const phantomSlack = phantomTh ? Math.round(phantomTh.getBoundingClientRect().width) : 0;
    const lastIndex = ths.length - 1;
    ths.forEach((th, i) => {
      const key = th.dataset.orderableColumn;
      const stored = storedWidths[key];
      let width = stored ?? measured[i];
      // Only fold the phantom slack in when we don't have a user-set width for
      // the last column; respecting persisted widths is more important.
      if (stored == null && i === lastIndex) width += phantomSlack;
      // A saved visibility preference may already hide this column. Leave its
      // natural width intact instead of freezing an unmeasurable column at zero.
      if (width > 0) th.style.width = `${width}px`;
    });
  }

  persistWidths() {
    const widths = {};
    this.draggableHeaders().forEach((th) => {
      const px = th.style.width;
      if (px) widths[th.dataset.orderableColumn] = parseInt(px, 10);
    });
    this.writeStored({ widths });
  }

  // appendChild moves existing nodes, so re-appending in left/keyed/right order
  // rebuilds the row in one pass.
  reorderRow(row, order) {
    const left = [];
    const right = [];
    const byKey = new Map();
    [...row.children].forEach((cell) => {
      if (cell.dataset.orderableColumn) byKey.set(cell.dataset.orderableColumn, cell);
      else if (cell.dataset.columnAnchor === "left") left.push(cell);
      else right.push(cell);
    });

    left.forEach((c) => row.appendChild(c));
    order.forEach((key) => {
      const cell = byKey.get(key);
      if (cell) {
        row.appendChild(cell);
        byKey.delete(key);
      }
    });
    byKey.forEach((cell) => row.appendChild(cell)); // keys not in stored order
    right.forEach((c) => row.appendChild(c));
  }

  bodyRows() {
    return [...this.element.querySelectorAll("tbody tr")];
  }

  draggableHeaders() {
    return [...this.headerRow.querySelectorAll(":scope > th[data-orderable-column]")];
  }

  currentHeaderKeys() {
    return this.draggableHeaders().map((th) => th.dataset.orderableColumn);
  }

  readStored() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.storageKey));
      if (Array.isArray(raw)) return { order: raw }; // legacy format
      return raw || {};
    } catch {
      return {};
    }
  }

  writeStored(updates) {
    const merged = { ...this.readStored(), ...updates };
    localStorage.setItem(this.storageKey, JSON.stringify(merged));
  }

  // Meta-tag fallback for off-request renders (ActionCable broadcasts have no server-set user id).
  get resolvedUserId() {
    return this.userIdValue || document.querySelector('meta[name="current-user-id"]')?.content || "";
  }

  get storageKey() {
    return `columnOrder.${this.tableValue}.v${this.versionValue}.user.${this.resolvedUserId}`;
  }
}
